import React, { useMemo, useState, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp, Search, Download } from 'lucide-react';
import { Citation, FlattenedConversation } from '../utils/dynamodb';

interface FlatConversationsTableProps {
  conversations: FlattenedConversation[];
  isLoading: boolean;
  onExportCSV: () => void;
  RefreshButton?: React.ReactNode;
}

const columnHelper = createColumnHelper<FlattenedConversation>();

export const FlatConversationsTable: React.FC<FlatConversationsTableProps> = ({
  conversations,
  isLoading,
  onExportCSV,
  RefreshButton,
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const [userIdFilter, setUserIdFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [feedbackFilter, setFeedbackFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const toggleCellExpansion = useCallback((cellId: string) => {
    console.log('Toggling cell:', cellId);
    setExpandedCells((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cellId)) {
        newSet.delete(cellId);
        console.log('Collapsed cell:', cellId);
      } else {
        newSet.add(cellId);
        console.log('Expanded cell:', cellId);
      }
      console.log('Current expanded cells:', Array.from(newSet));
      return newSet;
    });
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('title', {
        header: 'Conversation',
        cell: (info) => {
          const title = info.getValue();
          const cellId = `title-${info.row.index}`;
          const isExpanded = expandedCells.has(cellId);
          const shouldTruncate = title && title.length > 60;

          return (
            <div className="text-sm text-gray-900 font-medium">
              <div
                className={`${shouldTruncate ? 'cursor-pointer hover:bg-gray-50 p-1 rounded' : ''}`}
                onClick={() => shouldTruncate && toggleCellExpansion(cellId)}
                title={title}
              >
                {title
                  ? shouldTruncate && !isExpanded
                    ? `${title.substring(0, 60)}...`
                    : title
                  : 'Untitled Conversation'}
              </div>
            </div>
          );
        },
        size: 300,
        minSize: 200,
        maxSize: 400,
      }),
      columnHelper.accessor('session_id', {
        header: 'Session ID',
        cell: (info) => (
          <div className="font-mono text-xs" title={info.getValue()}>
            {info.getValue()}
          </div>
        ),
        size: 200,
        minSize: 150,
        maxSize: 300,
      }),
      columnHelper.accessor('user_id', {
        header: 'User ID',
        cell: (info) => {
          const userId = info.getValue();
          const cellId = `userId-${info.row.index}`;
          const isExpanded = expandedCells.has(cellId);
          const shouldTruncate = userId.length > 15;

          return (
            <div className="text-sm text-gray-700">
              <div
                className={`${shouldTruncate ? 'cursor-pointer hover:bg-gray-50 p-1 rounded' : ''}`}
                onClick={() => shouldTruncate && toggleCellExpansion(cellId)}
                title={userId}
              >
                {shouldTruncate && !isExpanded
                  ? `${userId.substring(0, 15)}...`
                  : userId}
              </div>
            </div>
          );
        },
        size: 120,
        minSize: 100,
        maxSize: 200,
      }),
      columnHelper.accessor('question', {
        header: 'Question',
        cell: (info) => {
          const question = info.getValue();
          const cellId = `question-${info.row.index}`;
          const isExpanded = expandedCells.has(cellId);
          const shouldTruncate = question.length > 80;

          return (
            <div className="text-sm text-gray-900">
              <div
                className={`${shouldTruncate ? 'cursor-pointer hover:bg-gray-50 p-1 rounded' : ''}`}
                onClick={() => shouldTruncate && toggleCellExpansion(cellId)}
                title={question}
              >
                {shouldTruncate && !isExpanded
                  ? `${question.substring(0, 80)}...`
                  : question}
              </div>
            </div>
          );
        },
        size: 300,
        minSize: 200,
        maxSize: 500,
      }),
      columnHelper.accessor('answer', {
        header: 'Answer',
        cell: (info) => {
          const answer = info.getValue();
          const cellId = `answer-${info.row.index}`;
          const isExpanded = expandedCells.has(cellId);
          const shouldTruncate = answer.length > 120;

          return (
            <div className="text-sm text-gray-700">
              <div
                className={`${shouldTruncate ? 'cursor-pointer hover:bg-gray-50 p-1 rounded' : ''}`}
                onClick={() => shouldTruncate && toggleCellExpansion(cellId)}
                title={answer}
              >
                {shouldTruncate && !isExpanded
                  ? `${answer.substring(0, 120)}...`
                  : answer}
              </div>
            </div>
          );
        },
        size: 400,
        minSize: 250,
        maxSize: 600,
      }),
      columnHelper.accessor('formatted_question_time', {
        header: 'Asked At',
        cell: (info) => {
          const time = info.getValue();
          const cellId = `questionTime-${info.row.index}`;
          const isExpanded = expandedCells.has(cellId);
          const shouldTruncate = time.length > 20;

          return (
            <div className="text-sm text-gray-500">
              <div
                className={`${shouldTruncate ? 'cursor-pointer hover:bg-gray-50 p-1 rounded' : ''}`}
                onClick={() => shouldTruncate && toggleCellExpansion(cellId)}
                title={time}
              >
                {shouldTruncate && !isExpanded
                  ? `${time.substring(0, 20)}...`
                  : time}
              </div>
            </div>
          );
        },
        size: 180,
        minSize: 140,
        maxSize: 220,
      }),
      columnHelper.accessor('formatted_answer_time', {
        header: 'Answered At',
        cell: (info) => {
          const time = info.getValue();
          const cellId = `answerTime-${info.row.index}`;
          const isExpanded = expandedCells.has(cellId);
          const shouldTruncate = time.length > 20;

          return (
            <div className="text-sm text-gray-500">
              <div
                className={`${shouldTruncate ? 'cursor-pointer hover:bg-gray-50 p-1 rounded' : ''}`}
                onClick={() => shouldTruncate && toggleCellExpansion(cellId)}
                title={time}
              >
                {shouldTruncate && !isExpanded
                  ? `${time.substring(0, 20)}...`
                  : time}
              </div>
            </div>
          );
        },
        size: 180,
        minSize: 140,
        maxSize: 220,
      }),
      columnHelper.accessor('citations', {
        header: 'Citations',
        cell: (info) => {
          const citations = info.getValue();
          if (!citations || citations.length === 0) {
            return <div className="text-sm text-gray-400">No citations</div>;
          }
          return (
            <div className="space-y-2">
              {citations.map((citation: Citation, idx) => (
                // show uri as filename and agent_source
                <div key={idx} className="text-sm text-gray-700">
                  {citation.uri.split('/').pop()} (Source:{' '}
                  {citation.agent_source || 'Unknown'})
                </div>
              ))}
            </div>
          );
        },
        size: 250,
        minSize: 150,
        maxSize: 400,
      }),
      columnHelper.accessor('feedback', {
        header: 'Feedback',
        cell: (info) => {
          const feedback = info.getValue();
          if (!feedback) {
            return <div className="text-sm text-gray-400">No feedback</div>;
          }

          const isPositive = feedback.rating === 1;
          return (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    isPositive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {isPositive ? '👍 Positive' : '👎 Negative'}
                </span>
              </div>
              {feedback.comment && (
                <div className="text-sm text-gray-600 italic whitespace-pre-wrap">
                  "{feedback.comment}"
                </div>
              )}
            </div>
          );
        },
        size: 200,
        minSize: 150,
        maxSize: 300,
      }),
    ],
    [expandedCells, toggleCellExpansion],
  );

  const filteredData = useMemo(() => {
    return conversations.filter((conversation) => {
      const matchesGlobal =
        globalFilter === '' ||
        conversation.question
          .toLowerCase()
          .includes(globalFilter.toLowerCase()) ||
        conversation.answer
          .toLowerCase()
          .includes(globalFilter.toLowerCase()) ||
        conversation.user_id
          .toLowerCase()
          .includes(globalFilter.toLowerCase()) ||
        conversation.session_id
          .toLowerCase()
          .includes(globalFilter.toLowerCase());

      const matchesUserId =
        userIdFilter === '' ||
        conversation.user_id.toLowerCase().includes(userIdFilter.toLowerCase());

      const matchesDateFrom =
        dateFromFilter === '' ||
        new Date(conversation.question_timestamp) >= new Date(dateFromFilter);

      const matchesDateTo =
        dateToFilter === '' ||
        new Date(conversation.question_timestamp) <=
          new Date(dateToFilter + 'T23:59:59');

      const matchesFeedback =
        feedbackFilter === '' ||
        (feedbackFilter === 'positive' &&
          conversation.feedback?.rating === 1) ||
        (feedbackFilter === 'negative' &&
          conversation.feedback?.rating === 0) ||
        (feedbackFilter === 'none' && !conversation.feedback);

      return (
        matchesGlobal &&
        matchesUserId &&
        matchesDateFrom &&
        matchesDateTo &&
        matchesFeedback
      );
    });
  }, [
    conversations,
    globalFilter,
    userIdFilter,
    dateFromFilter,
    dateToFilter,
    feedbackFilter,
  ]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 20,
      },
      sorting: [
        {
          id: 'question_timestamp',
          desc: true,
        },
      ],
    },
  });

  const clearFilters = () => {
    setGlobalFilter('');
    setUserIdFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setFeedbackFilter('');
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg flex flex-col h-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Question & Answer Conversations
          </h3>
        </div>
        <div className="flex-1 p-6">
          <div className="animate-pulse">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              View All User Conversations ({filteredData.length})
            </h3>
          </div>
          <div className="flex items-center gap-3">
            {RefreshButton}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <Search className="h-4 w-4 mr-2" />
              Search
            </button>
            <button
              onClick={onExportCSV}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Questions & Answers
              </label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search content..."
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User ID
              </label>
              <input
                type="text"
                placeholder="Filter by user ID"
                value={userIdFilter}
                onChange={(e) => setUserIdFilter(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Feedback
              </label>
              <select
                value={feedbackFilter}
                onChange={(e) => setFeedbackFilter(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="">All feedback</option>
                <option value="positive">👍 Positive only</option>
                <option value="negative">👎 Negative only</option>
                <option value="none">No feedback</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}

      {/* Pagination (moved above table) */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing{' '}
            {table.getState().pagination.pageIndex *
              table.getState().pagination.pageSize +
              1}{' '}
            to{' '}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) *
                table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length,
            )}{' '}
            of {table.getFilteredRowModel().rows.length} results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-hidden">
        <div className="overflow-auto h-full">
          <table
            className="w-full divide-y divide-gray-200"
            style={{ width: table.getCenterTotalSize() }}
          >
            <thead className="bg-gray-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="sticky top-0 z-30 bg-gray-50"
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 relative"
                      style={{
                        width: header.getSize(),
                      }}
                    >
                      <div
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getIsSorted() && (
                          <span>
                            {header.column.getIsSorted() === 'desc' ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronUp className="h-4 w-4" />
                            )}
                          </span>
                        )}
                      </div>
                      {header.column.getCanResize() && (
                        <div
                          className="absolute right-0 top-0 h-full w-1 bg-gray-300 cursor-col-resize hover:bg-blue-500 opacity-0 hover:opacity-100"
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-6 py-4 text-sm"
                      style={{
                        width: cell.column.getSize(),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
